/**
 * Collapsible "Thinking..." indicator for AI chat.
 *
 * Shows animated dots with a "Thinking..." label. When tool events
 * are active, displays a count and expands on click to reveal tool
 * call badges — keeping the default view clean and compact.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { ToolCallBlock } from './ToolCallBlock';
import type { ToolEvent } from './types';

interface ThinkingDotsProps {
  /** Live tool events to display in the collapsible area */
  toolEvents?: ToolEvent[];
  /** Legacy tool status string (when no structured events) */
  toolStatus?: string | null;
  /** Live reasoning/thinking content streamed from a reasoning-capable model
   *  (DeepSeek R1, Qwen3 Max, GLM-4.6, …). Shown inside the collapsible area
   *  so users can see what the model is thinking instead of staring at a
   *  silent spinner for minutes. */
  reasoning?: string;
}

export function ThinkingDots({ toolEvents = [], toolStatus, reasoning }: ThinkingDotsProps) {
  const { t } = useTranslation('aiChat');
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const hasEvents = toolEvents.length > 0;
  const hasRunning = toolEvents.some(e => e.status === 'running');
  const hasReasoning = !!reasoning && reasoning.length > 0;
  const hasContent = hasEvents || !!toolStatus || hasReasoning;
  // Auto-expand when tools are actively running OR a reasoning model is
  // streaming thinking — both are live signals the user wants to see.
  const expanded = manualToggle !== null ? manualToggle : (hasRunning || hasReasoning);

  // Auto-scroll the reasoning panel to the bottom as new tokens arrive.
  const reasoningEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (expanded && hasReasoning) {
      reasoningEndRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [reasoning, expanded, hasReasoning]);

  return (
    <div>
      {/* Header row */}
      <button
        type="button"
        onClick={() => hasContent && setManualToggle(expanded ? false : true)}
        className={cn(
          'flex items-center gap-2',
          hasContent && 'cursor-pointer hover:opacity-80',
        )}
      >
        <div className="flex items-center gap-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:-0.3s]" />
          <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:-0.15s]" />
          <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" />
        </div>
        <span className="text-sm text-muted-foreground">
          {hasRunning ? t('usingTools') : t('thinking')}
        </span>
        {hasReasoning && !hasRunning && (
          <span className="text-xs text-muted-foreground/70">
            ({reasoning!.length.toLocaleString()} {t('thinkingChars')})
          </span>
        )}
        {hasContent && (
          <ChevronRight className={cn(
            'h-3 w-3 text-muted-foreground transition-transform',
            expanded && 'rotate-90',
          )} />
        )}
      </button>

      {/* Collapsible tool events */}
      {expanded && hasEvents && (
        <div className="mt-2 space-y-1 pl-5">
          {toolEvents.map((event, i) => (
            <ToolCallBlock
              key={`${event.toolName}-${i}`}
              toolEvent={event}
            />
          ))}
        </div>
      )}

      {/* Legacy tool status fallback */}
      {expanded && toolStatus && !hasEvents && (
        <p className="mt-1.5 pl-5 text-xs text-muted-foreground italic">{toolStatus}</p>
      )}

      {/* Reasoning trail (collapsible). Capped-height scroll panel so a
       *  long reasoning trace doesn't push the rest of the chat off-screen. */}
      {expanded && hasReasoning && (
        <div className="mt-2 pl-5">
          <div className="max-h-48 overflow-y-auto rounded border border-border/50 bg-muted/30 p-2">
            <pre className="whitespace-pre-wrap text-xs text-muted-foreground font-sans leading-relaxed">
              {reasoning}
            </pre>
            <div ref={reasoningEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}
